import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AdminLayout from "../components/AdminLayout";
import { listAdminProducts, setProductFeatured, type AdminProduct } from "@/lib/admin";

export default function AdminContenidoPage() {
  const { t } = useTranslation("admin");
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listAdminProducts({ search: search.trim() || undefined })
      .then((data) => { if (!cancelled) setProducts(data); })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search]);

  const toggle = async (p: AdminProduct) => {
    setBusyId(p.id);
    const next = !p.isFeatured;
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, isFeatured: next } : x)));
    try {
      await setProductFeatured(p.id, next);
    } catch {
      setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, isFeatured: !next } : x)));
    } finally {
      setBusyId(null);
    }
  };

  const featuredCount = products.filter((p) => p.isFeatured).length;

  return (
    <AdminLayout title={t("admin.content.title")}>
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-bold text-charcoal">{t("admin.content.heading")}</h2>
          <p className="text-sm text-gray-500">{featuredCount} · {t("admin.content.featured")}</p>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("admin.content.search")}
          className="w-full sm:w-72 border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-ocean"
        />
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.content.tour")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3 pr-4">{t("admin.content.provider")}</th>
              <th className="text-left text-xs font-medium text-gray-400 py-3">{t("admin.content.featured")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={3} className="py-10 text-center text-gray-400">{t("admin.loading")}</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={3} className="py-10 text-center text-gray-400">{t("admin.content.empty")}</td></tr>
            ) : (
              products.map((p) => (
                <tr key={p.id} className="border-b border-gray-50 last:border-0">
                  <td className="py-3 pr-4 font-medium text-charcoal">{p.title}</td>
                  <td className="py-3 pr-4 text-gray-600">{p.providerName}</td>
                  <td className="py-3">
                    <button
                      onClick={() => toggle(p)}
                      disabled={busyId === p.id}
                      className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${p.isFeatured ? "bg-ocean" : "bg-gray-200"}`}
                      aria-pressed={p.isFeatured}
                      aria-label={t("admin.content.featureTour")}
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${p.isFeatured ? "right-1" : "left-1"}`} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
